import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useSellOfferList } from './hooks'
import { SellOffer } from './types'
import { CButton, CCardBody } from '@coreui/react-pro'
import { Badge, InputRef, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { useStore } from 'effector-react'
import { $userRole } from '../../../../features/user'
import dayjs from 'dayjs'
import {
  getColumnSearchDateProps,
  getColumnSearchProps,
} from '../../tableFunctions'
import { path, role } from '../../../../libs'
import { convertSellOfferStatus, statusFilter } from './consts'
import { Order } from '../../orders/list/types'
import {
  $sellOffersWithoutPagination,
  sellOfferListWithoutPaginationMounted,
} from './model'
import { getNumberFormat } from '../../../../libs/utils'

export const SellOfferList = () => {
  const { sellOffers, totalCountOfSellOffers, loading, onChangeSellOfferList } =
    useSellOfferList()

  const sellOffersWithoutPagination = useStore($sellOffersWithoutPagination)

  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [searchedColumn, setSearchedColumn] = useState('')
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})

  const userRole = useStore($userRole)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  useEffect(() => {
    sellOfferListWithoutPaginationMounted()
  }, [])

  useEffect(() => {
    onChangeSellOfferList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const getCsvCode = () => {
    if (sellOffersWithoutPagination.length == 0) {
      return 'data:text/csv;charset=utf-8,'
    }
    const csvContent = sellOffersWithoutPagination.map((item) => {
      return {
        ID: item.id,
        'Property Name': item.propertyName,
        'Numbers of tokens': +item.tokenAmount,
        'Initial token price': `${+item.initialTokenPrice} usd`,
        'Seller token price': `${+item.tokenPriceMaker} usd`,
        'Total sell amount': `${+item.totalPriceMaker} usd`,
        'NOVA % commission': +item.tokenPriceFeePercent,
        'NOVA % commission $': `${+item.novaUSDTotalFee} usd`,
        'Number of sells': item.numberOfSells,
        'Seller User ID': item.clientId,
        Status: item.status,
        'Started at': new Date(item.startedAt)
          .toLocaleString()
          .replace(',', ' '),
        'Closed at': new Date(item.closedAt).toLocaleString().replace(',', ' '),
      }
    })
    const header = Object.keys(csvContent[0])?.join(',')
    const data = csvContent.map((item) => Object.values(item).join(','))
    return (
      'data:text/csv;charset=utf-8,' +
      encodeURIComponent([header, ...data].join('\n'))
    )
  }

  const columns: ColumnsType<SellOffer> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps<SellOffer>(
        'id',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: (a, b) => parseInt(a.id) - parseInt(b.id),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
    },
    {
      title: 'Numbers of tokens',
      dataIndex: 'tokenAmount',
      render: (item) => <div>{+item}</div>,
    },
    {
      title: 'Initial token price',
      dataIndex: 'initialTokenPrice',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Seller token price',
      dataIndex: 'tokenPriceMaker',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'Total sell amount',
      dataIndex: 'totalPriceMaker',
      render: (item) => <div>{+item + ' USD'}</div>,
    },
    {
      title: 'NOVA % commission',
      dataIndex: 'tokenPriceFeePercent',
      render: (item) => <div>{+item}</div>,
    },
    {
      title: 'NOVA $ commission',
      dataIndex: 'novaUSDTotalFee',
      render: (item) => <div>{getNumberFormat(item, 2) + ' USD'}</div>,
    },
    {
      title: 'Number of sells',
      dataIndex: 'numberOfSells',
    },
    {
      title: 'Seller User ID',
      ...getColumnSearchProps<SellOffer>(
        'clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (item: SellOffer) => (
        <div>
          {item.user?.isTest ? (
            <Badge count={'Test'} offset={[10, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item.clientId}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item.clientId}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filters: statusFilter,
      filterSearch: true,
      onFilter: (value: any, record) =>
        record.status.toString().includes(value),
      render: (item: string) => (
        <>
          <Tag
            color={
              convertSellOfferStatus(item) == 'completed'
                ? '#2eb85c'
                : '#9e9cad'
            }
          >
            {convertSellOfferStatus(item)}
          </Tag>
        </>
      ),
    },
    {
      title: 'Started at',
      dataIndex: 'startedAt',
      ...getColumnSearchDateProps<SellOffer>(
        'startedAt',
        setSearchText,
        searchText,
        setIsCalendarOpen,
        isCalendarOpen,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
      width: '150px',
    },
    {
      title: 'Closed at',
      dataIndex: 'closedAt',
      ...getColumnSearchDateProps<SellOffer>(
        'closedAt',
        setSearchText,
        searchText,
        setIsCalendarOpen,
        isCalendarOpen,
        searchInput,
      ),
      render: (item) => <div>{new Date(item).toLocaleString()}</div>,
      sorter: true,
      width: '150px',
    },
  ]

  const isDisplayDownloadButton =
    userRole?.filter((roleItem) =>
      [
        role.root(),
        role.head.cfo(),
        role.finances.financeManager(),
        role.nova(),
      ].includes(roleItem),
    ).length !== 0

  return (
    <CCardBody>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          {isDisplayDownloadButton && (
            <CButton
              color="primary"
              href={getCsvCode()}
              download={
                'orders ' +
                dayjs(Date.now()).format('YYYY-MM-DD HH-mm') +
                '.csv'
              }
              target="_blank"
            >
              Download current items (.csv)
            </CButton>
          )}
        </div>
      </div>
      <Table
        dataSource={sellOffers}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        onRow={(record, rowIndex) => {
          return {
            onClick: (event) => {},
          }
        }}
        rowKey="id"
        columns={columns}
        pagination={{
          showSizeChanger: true,
          total: totalCountOfSellOffers,
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters: { ...filtersState, ...filters },
          })
        }
      />
    </CCardBody>
  )
}
